import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * State master (Masters → States). Belongs to a country; unique on
 * (countryId, name). See ADR-0016 for the standard master shape.
 */

const CountryRefSchema = z.object({ id: UuidSchema, isoCode: z.string(), name: z.string() });

export const StateListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  isoCode: z.string().nullable(),
  country: CountryRefSchema,
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type StateListItem = z.infer<typeof StateListItemSchema>;

export const StateListQuerySchema = PaginationQuerySchema.extend({
  countryId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type StateListQuery = z.infer<typeof StateListQuerySchema>;

export const StateListResponseSchema = paginatedResponseSchema(StateListItemSchema);
export type StateListResponse = z.infer<typeof StateListResponseSchema>;

export const StateDetailSchema = StateListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type StateDetail = z.infer<typeof StateDetailSchema>;

const StateCoreFieldsSchema = z.object({
  countryId: UuidSchema.describe('Country UUID. Required; use GET /countries/options.'),
  name: z.string().trim().min(1, 'Name is required').max(120),
  /** ISO 3166-2 subdivision code where available (e.g. "IN-MH"). */
  isoCode: z.string().trim().max(10).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateStateRequestSchema = StateCoreFieldsSchema;
export type CreateStateRequest = z.infer<typeof CreateStateRequestSchema>;

export const UpdateStateRequestSchema = StateCoreFieldsSchema.partial();
export type UpdateStateRequest = z.infer<typeof UpdateStateRequestSchema>;
